import React, { useState, useEffect } from 'react';
import { useAuth } from '../../lib/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/Button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/Card';
import { Input } from '../ui/Input';
import { 
  ArrowLeft, 
  Search, 
  Filter, 
  Eye, 
  Users, 
  MessageSquare, 
  Calendar,
  ChevronDown,
  Download,
  ClipboardCheck
} from 'lucide-react';

interface TeamMember {
  id: string;
  displayName: string;
  email: string;
  photoURL?: string;
  role: string;
  managers?: Array<{
    id: string;
    name: string;
    email: string;
  }>;
  reviewCount: number;
  lastReviewDate?: string;
  upForReview: boolean;
  pulseCheckCompleted: boolean;
  pulseCheckSubmissionId?: string;
}

interface ReviewSubmission {
  id: string;
  revieweeId: string;
  revieweeName: string;
  revieweeEmail: string;
  reviewerId: string;
  reviewerName?: string;
  reviewerEmail?: string;
  isAnonymous: boolean;
  responses: { [questionId: string]: any };
  templateId: string;
  templateName: string;
  submittedAt: any;
  status: string;
}

const ReviewManagement: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [reviewSubmissions, setReviewSubmissions] = useState<ReviewSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMember, setSelectedMember] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  // Check permissions - super admins, admins, and HR can see all users
  const superAdmins = ['hunter@stitchdesignco.com', 'laine@stitchdesignco.com'];
  const isSuperAdminUser = currentUser?.email && superAdmins.includes(currentUser.email.toLowerCase());
  const isAdmin = isSuperAdminUser || userProfile?.role === 'admin';
  const isHR = userProfile?.isHR === true;
  const hasFullAccess = isSuperAdminUser || isAdmin || isHR;

  useEffect(() => {
    fetchData();
  }, [currentUser]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const { collection, getDocs, query, where, orderBy } = await import('firebase/firestore');
      const { db } = await import('../../lib/firebase');
      
      // Fetch all users
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      
      // Fetch all review submissions
      const reviewsRef = collection(db, 'reviewSubmissions');
      const reviewsQuery = query(reviewsRef, orderBy('submittedAt', 'desc'));
      const reviewsSnapshot = await getDocs(reviewsQuery);
      
      // Fetch all engagement pulse submissions
      const pulseRef = collection(db, 'engagementPulseSubmissions');
      const pulseSnapshot = await getDocs(pulseRef);
      
      const pulseSubmissions = pulseSnapshot.docs.reduce((acc, doc) => {
        const data = doc.data();
        // Store submission ID by user ID
        acc[data.userId] = doc.id;
        return acc;
      }, {} as { [userId: string]: string });
      
      const allUsers = usersSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          displayName: data.displayName,
          email: data.email,
          photoURL: data.photoURL,
          role: data.role,
          managers: data.managers,
          upForReview: data.upForReview,
          ...data
        };
      });
      
      const allReviews = reviewsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ReviewSubmission[];
      
      // Filter team members based on user role
      let filteredUsers = allUsers;
      
      if (!hasFullAccess) {
        // For managers (without admin/HR), only show their direct reports
        filteredUsers = allUsers.filter(user => 
          user.managers?.some((manager: any) => manager.id === currentUser?.uid)
        );
      }
      
      // Calculate review counts for each team member
      const teamMembersWithCounts = filteredUsers.map(user => {
        const userReviews = allReviews.filter(review => review.revieweeId === user.id);
        const lastReview = userReviews[0]; // Most recent due to orderBy
        
        return {
          id: user.id,
          displayName: user.displayName || user.email?.split('@')[0] || 'Unknown',
          email: user.email || '',
          photoURL: user.photoURL,
          role: user.role || 'employee',
          managers: user.managers || [],
          reviewCount: userReviews.length,
          lastReviewDate: lastReview?.submittedAt?.toDate?.()?.toLocaleDateString() || undefined,
          upForReview: user.upForReview || false,
          pulseCheckCompleted: !!pulseSubmissions[user.id],
          pulseCheckSubmissionId: pulseSubmissions[user.id]
        };
      });
      
      // Filter reviews based on user permissions
      let filteredReviews = allReviews;
      
      if (!hasFullAccess) {
        // For managers (without admin/HR), only show reviews for their direct reports
        const managedUserIds = filteredUsers.map(user => user.id);
        filteredReviews = allReviews.filter(review => 
          managedUserIds.includes(review.revieweeId)
        );
      }
      
      setTeamMembers(teamMembersWithCounts);
      setReviewSubmissions(filteredReviews);
      
    } catch (err: any) {
      console.error('Error fetching review data:', err);
      setError('Failed to load review data');
    } finally {
      setLoading(false);
    }
  };

  const filteredMembers = teamMembers.filter(member =>
    member.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredReviews = reviewSubmissions.filter(review => {
    if (selectedMember === 'all') return true;
    return review.revieweeId === selectedMember;
  });

  const handleViewReviews = (memberId: string) => {
    navigate(`/admin/reviews/${memberId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading review data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="text-center py-8">
            <div className="text-destructive mb-4">
              <MessageSquare className="h-16 w-16 mx-auto mb-4 opacity-50" />
            </div>
            <h3 className="text-lg font-medium mb-2">Unable to Load Reviews</h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => navigate(hasFullAccess ? '/admin' : '/dashboard')} variant="outline">
              {hasFullAccess ? 'Return to Admin Panel' : 'Return to Dashboard'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate(hasFullAccess ? '/admin' : '/dashboard')} className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                {hasFullAccess ? 'Admin Panel' : 'Dashboard'}
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-primary">Review Management</h1>
                <p className="text-muted-foreground">
                  {hasFullAccess ? 'View all peer review responses' : 'View reviews for your team members'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" className="flex items-center gap-2">
                <Download className="h-4 w-4" />
                Export Data
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Team Members</p>
                  <p className="text-2xl font-bold">{teamMembers.length}</p>
                </div>
                <Users className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Total Reviews</p>
                  <p className="text-2xl font-bold">{reviewSubmissions.length}</p>
                </div>
                <MessageSquare className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Up for Peer Review</p>
                  <p className="text-2xl font-bold">{teamMembers.filter(m => m.upForReview).length}</p>
                </div>
                <Calendar className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filters */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search team members..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex gap-2">
                <select
                  value={selectedMember}
                  onChange={(e) => setSelectedMember(e.target.value)}
                  className="px-3 py-2 border border-input rounded-md bg-background"
                >
                  <option value="all">All Team Members</option>
                  {teamMembers.map(member => (
                    <option key={member.id} value={member.id}>
                      {member.displayName}
                    </option>
                  ))}
                </select>
                <Button
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2"
                >
                  <Filter className="h-4 w-4" />
                  Filters
                  <ChevronDown className={`h-4 w-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Team Members Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMembers.map((member) => (
            <Card key={member.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-4">
                  {member.photoURL ? (
                    <img 
                      src={member.photoURL} 
                      alt={member.displayName} 
                      className="w-12 h-12 rounded-full object-cover" 
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const fallback = target.nextElementSibling as HTMLElement;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center"
                    style={{ display: member.photoURL ? 'none' : 'flex' }}
                  >
                    <span className="text-primary font-semibold text-lg">
                      {member.displayName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-lg">{member.displayName}</CardTitle>
                    <CardDescription className="text-sm">{member.email}</CardDescription>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        member.role === 'manager' ? 'bg-blue-100 text-blue-800' : 
                        member.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                        'bg-secondary text-secondary-foreground'
                      }`}>
                        {member.role}
                      </span>
                      {member.upForReview && (
                        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                          Up for Peer Review
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="pt-0">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Reviews Received:</span>
                    <span className="font-semibold">{member.reviewCount}</span>
                  </div>
                  
                  {member.lastReviewDate && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Last Review:</span>
                      <span className="text-sm">{member.lastReviewDate}</span>
                    </div>
                  )}
                  
                  {member.managers && member.managers.length > 0 && (
                    <div className="text-xs text-muted-foreground">
                      Reports to: {member.managers.map(m => m.name).join(', ')}
                    </div>
                  )}
                  
                  <div className="flex flex-col gap-2 mt-4">
                    <Button 
                      className="w-full flex items-center gap-2"
                      onClick={() => handleViewReviews(member.id)}
                      disabled={member.reviewCount === 0}
                    >
                      <Eye className="h-4 w-4" />
                      View Reviews ({member.reviewCount})
                    </Button>
                    <Button 
                      variant="outline"
                      className="w-full flex items-center gap-2"
                      onClick={() => navigate(`/admin/pulse-response/${member.pulseCheckSubmissionId}`)}
                      disabled={!member.pulseCheckCompleted}
                    >
                      <ClipboardCheck className="h-4 w-4" />
                      {member.pulseCheckCompleted ? 'View Pulse Response' : 'No Pulse Response'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredMembers.length === 0 && (
          <Card>
            <CardContent className="text-center py-12">
              <Users className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium mb-2">No Team Members Found</h3>
              <p className="text-muted-foreground">
                {searchTerm ? 'Try adjusting your search terms.' : 'No team members match the current filters.'}
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default ReviewManagement;
